import { jsPDF } from "jspdf";

interface Medication { name: string; notes?: string; }
interface Allergy { name: string; notes?: string; }
interface Condition { name: string; notes?: string; attackStatus?: string; }

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  blood_type?: string;
  emergency_contacts?: Array<{ name: string; phone: string; relation: string }>;
  medications?: Medication[];
  allergies?: Allergy[];
  conditions?: Condition[];
  additional_notes?: string;
  photo_url: string;
}

export const exportProfileToPDF = async (profile: Profile, qrCodeDataUrl: string) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(79, 70, 229); // Primary color
  doc.text("NOTFALLPROFIL", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 15;

  // Divider
  doc.setDrawColor(229, 231, 235);
  doc.line(20, yPosition, pageWidth - 20, yPosition);
  yPosition += 10;

  // Profile Photo
  try {
    if (profile.photo_url) {
      doc.addImage(profile.photo_url, "JPEG", 20, yPosition, 40, 40);
    }
  } catch (error) {
    console.error("Error adding profile photo:", error);
  }

  // Basic Info
  doc.setFontSize(18);
  doc.setTextColor(0, 0, 0);
  doc.text(`${profile.first_name} ${profile.last_name}`, 70, yPosition + 10);
  
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  
  if (profile.date_of_birth) {
    const birthDate = new Date(profile.date_of_birth).toLocaleDateString("de-DE");
    doc.text(`Geburtsdatum: ${birthDate}`, 70, yPosition + 20);
  }
  
  if (profile.blood_type) {
    doc.setTextColor(220, 38, 38); // Red for blood type
    doc.setFontSize(12);
    doc.text(`Blutgruppe: ${profile.blood_type}`, 70, yPosition + 30);
  }
  
  yPosition += 50;

  // Emergency Contacts Section
  if (profile.emergency_contacts && profile.emergency_contacts.length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("🚨 NOTFALLKONTAKTE", 20, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    
    profile.emergency_contacts.forEach((contact) => {
      if (contact.name && contact.phone) {
        const text = `• ${contact.name} (${contact.relation}): ${contact.phone}`;
        doc.text(text, 25, yPosition);
        yPosition += 6;
      }
    });
    yPosition += 5;
  }

  // Check if we need a new page
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  // Medications Section
  if (profile.medications && profile.medications.filter(m => m.name).length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("💊 MEDIKAMENTE", 20, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    
    profile.medications.filter(m => m.name).forEach((medication) => {
      doc.text(`• ${medication.name}`, 25, yPosition);
      yPosition += 6;
      if (medication.notes) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        const noteLines = doc.splitTextToSize(`  ${medication.notes}`, pageWidth - 55);
        noteLines.forEach((line: string) => {
          doc.text(line, 25, yPosition);
          yPosition += 5;
        });
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
      }
    });
    yPosition += 5;
  }

  // Check if we need a new page
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  // Allergies Section
  if (profile.allergies && profile.allergies.filter(a => a.name).length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(220, 38, 38); // Red for allergies
    doc.text("⚠️ ALLERGIEN", 20, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(220, 38, 38);
    
    profile.allergies.filter(a => a.name).forEach((allergy) => {
      doc.text(`• ${allergy.name}`, 25, yPosition);
      yPosition += 6;
      if (allergy.notes) {
        doc.setFontSize(9);
        doc.setTextColor(180, 38, 38);
        const noteLines = doc.splitTextToSize(`  ${allergy.notes}`, pageWidth - 55);
        noteLines.forEach((line: string) => {
          doc.text(line, 25, yPosition);
          yPosition += 5;
        });
        doc.setFontSize(10);
        doc.setTextColor(220, 38, 38);
      }
    });
    yPosition += 5;
    
    doc.setTextColor(50, 50, 50); // Reset color
  }

  // Check if we need a new page
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  // Medical Conditions Section
  if (profile.conditions && profile.conditions.filter(c => c.name).length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("🏥 VORERKRANKUNGEN", 20, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    
    profile.conditions.filter(c => c.name).forEach((condition) => {
      let text = `• ${condition.name}`;
      if (condition.attackStatus) {
        const statusText = condition.attackStatus === 'akut' ? 'Akuter Schub' : 
                          condition.attackStatus === 'remission' ? 'In Remission' : 'Chronisch stabil';
        text += ` (${statusText})`;
      }
      doc.text(text, 25, yPosition);
      yPosition += 6;
      if (condition.notes) {
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        const noteLines = doc.splitTextToSize(`  ${condition.notes}`, pageWidth - 55);
        noteLines.forEach((line: string) => {
          doc.text(line, 25, yPosition);
          yPosition += 5;
        });
        doc.setFontSize(10);
        doc.setTextColor(50, 50, 50);
      }
    });
    yPosition += 5;
  }

  // Check if we need a new page
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  // Additional Notes Section
  if (profile.additional_notes) {
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("📝 ZUSÄTZLICHE NOTIZEN", 20, yPosition);
    yPosition += 8;
    
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    
    // Split text into lines that fit the page width
    const maxWidth = pageWidth - 50;
    const lines = doc.splitTextToSize(profile.additional_notes, maxWidth);
    lines.forEach((line: string) => {
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
      }
      doc.text(line, 25, yPosition);
      yPosition += 6;
    });
    yPosition += 10;
  }

  // Add new page for QR Code if needed
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  // QR Code Section
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text("QR-CODE FÜR SCHNELLZUGRIFF", pageWidth / 2, yPosition, { align: "center" });
  yPosition += 10;
  
  // Add QR Code
  const qrSize = 60;
  const qrX = (pageWidth - qrSize) / 2;
  doc.addImage(qrCodeDataUrl, "PNG", qrX, yPosition, qrSize, qrSize);
  yPosition += qrSize + 5;
  
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(
    "Scannen Sie diesen QR-Code für sofortigen Zugriff auf dieses Notfallprofil",
    pageWidth / 2,
    yPosition,
    { align: "center", maxWidth: pageWidth - 40 }
  );

  // Footer on all pages
  const totalPages = doc.internal.pages.length - 1; // Subtract 1 because of the first empty page
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    const footerText = `Erstellt am ${new Date().toLocaleDateString("de-DE")} | Seite ${i} von ${totalPages}`;
    doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: "center" });
  }

  // Save PDF
  doc.save(`notfallprofil-${profile.first_name}-${profile.last_name}.pdf`);
};
